/**
 * R2 Floating Measurement Palette
 * Persistent mini-panel showing: current value, running total, average,
 * max, active layer/collection, quick activate button.
 * Always visible (draggable) from any page when dismissed from overlay.
 */
import { useState } from 'react';
import { Ruler, ChevronUp, ChevronDown, Clipboard, Layers, Zap } from 'lucide-react';
import {
  useR2MeasurementStore, LAYER_COLORS, formatValue,
} from '../../lib/r2-measurement/r2-measurement.store.js';
import { FloatingWindowFrame } from '../windowing/FloatingWindowFrame.js';

export function R2FloatingMeasurePalette() {
  const [collapsed, setCollapsed] = useState(false);
  const {
    measurements, collections, activeLayer, activeCollection,
    lastValue, overlayActive, setOverlayActive, setActiveTool, currentPage,
    calibrations, activeUnit,
  } = useR2MeasurementStore();

  const cal = calibrations[currentPage];
  const layerColor = LAYER_COLORS[activeLayer] ?? '#5eead4';

  // Measurements on current page
  const pageMeas = measurements.filter((m) => m.page === currentPage);
  const allValues = pageMeas.map((m) => m.calibratedValue ?? m.value);
  const total = allValues.reduce((s, v) => s + v, 0);
  const avg   = allValues.length ? total / allValues.length : 0;
  const max   = allValues.length ? Math.max(...allValues) : 0;
  const unit  = cal?.unit ?? 'px';

  // Active collection total
  const activeCol = collections.find((c) => c.id === activeCollection);
  const colTotal  = activeCol ? measurements
    .filter((m) => activeCol.measurements.includes(m.id))
    .reduce((s, m) => s + (m.calibratedValue ?? m.value), 0) : null;

  const copyLast = () => {
    if (lastValue) navigator.clipboard.writeText(lastValue).catch(() => {});
  };

  return (
    <FloatingWindowFrame
      id="measurement-palette"
      title="Document Measurement"
      group="Tools"
      defaultRect={{ x: Math.max(16, window.innerWidth - 236), y: Math.max(80, window.innerHeight - 320), width: 220, height: collapsed ? 76 : 260 }}
      defaultOpen
      canMaximize={false}
      keyboardClosable
      important
      accent={layerColor}
      contentStyle={{ overflow: 'hidden' }}
    >
    <div
      data-testid="r2-measure-palette"
      style={{
        height: '100%',
        background: 'rgba(6,10,20,0.97)', border: `1px solid ${layerColor}55`,
        padding: collapsed ? '8px 14px' : '12px 14px',
        minWidth: 200, backdropFilter: 'blur(14px)',
        transition: 'all .2s',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: collapsed ? 0 : 10 }}>
        <div style={{ width: 6, height: 6, borderRadius: 2, background: layerColor, flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: layerColor, letterSpacing: '0.1em', textTransform: 'uppercase', flex: 1 }}>
          SME · {activeLayer}
        </span>
        {!overlayActive && (
          <button
            data-testid="sme-activate-btn"
            onClick={() => { setOverlayActive(true); setActiveTool('distance'); }}
            style={{ height: 22, padding: '0 8px', borderRadius: 5, border: `1px solid ${layerColor}66`, background: `${layerColor}15`, color: layerColor, fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em' }}>
            M
          </button>
        )}
        {overlayActive && (
          <div style={{ fontSize: 9, color: layerColor, background: `${layerColor}15`, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
            ACTIVE
          </div>
        )}
        <button onClick={() => setCollapsed((v) => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(148,163,184,0.6)', padding: 0 }}>
          {collapsed ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Last value */}
          {lastValue && (
            <div style={{ background: `${layerColor}10`, borderRadius: 7, padding: '6px 10px', marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: `${layerColor}88`, marginBottom: 2, letterSpacing: '0.1em' }}>LAST MEASUREMENT</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: layerColor, fontFamily: 'monospace' }}>{lastValue}</span>
                <button onClick={copyLast}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: `${layerColor}88` }}>
                  <Clipboard className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* Stats grid */}
          {pageMeas.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 8 }}>
              {[
                { label: 'TOTAL', value: formatValue(total, unit) },
                { label: 'AVG',   value: formatValue(avg,   unit) },
                { label: 'MAX',   value: formatValue(max,   unit) },
              ].map((s) => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 6, padding: '4px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: 8, color: 'rgba(148,163,184,0.45)', letterSpacing: '0.1em' }}>{s.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#e2ecfa', fontFamily: 'monospace' }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Active collection */}
          {activeCol && colTotal !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: 'rgba(255,255,255,0.02)', borderRadius: 7, marginBottom: 6 }}>
              <Layers className="h-3 w-3" style={{ color: activeCol.color, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeCol.name}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: activeCol.color, fontFamily: 'monospace' }}>{formatValue(colTotal, unit)}</span>
            </div>
          )}

          {/* Count */}
          <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.4)', textAlign: 'center', borderTop: '1px solid rgba(37,181,168,0.06)', paddingTop: 6 }}>
            {pageMeas.length} measurement{pageMeas.length !== 1 ? 's' : ''} on this page · {measurements.length} total
            {!cal && <span style={{ color: '#fbbf24', marginLeft: 6 }}>⚠ not calibrated</span>}
          </div>
        </>
      )}
    </div>
    </FloatingWindowFrame>
  );
}
